import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, ChevronDown, User } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      // Navigation is handled by the auth state change listener
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <nav className="bg-white shadow-sm py-4 border-b">
      <div className="container mx-auto px-4 flex justify-between items-center">
        <Link to="/" className="flex items-center space-x-2">
          <span className="text-2xl font-bold bg-gradient-to-r from-brand-blue to-brand-purple bg-clip-text text-transparent">SEO Link Market</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-6">
          <div className="relative group">
            <button className="flex items-center space-x-1 text-gray-700 hover:text-brand-blue">
              <span>For Buyers</span>
              <ChevronDown size={16} />
            </button>
            <div className="absolute h-4 w-full"></div>
            <div className="absolute z-10 invisible group-hover:visible hover:visible bg-white shadow-md rounded-md w-48 mt-1 py-2">
              <Link to="/marketplace" className="block px-4 py-2 hover:bg-gray-50">Marketplace</Link>
              <Link to="/custom-packages" className="block px-4 py-2 hover:bg-gray-50">Custom Packages</Link>
              <Link to="/buyer-requests" className="block px-4 py-2 hover:bg-gray-50">Buyer Requests</Link>
              <Link to="/sellers" className="block px-4 py-2 hover:bg-gray-50">Find Sellers</Link>
            </div>
          </div>

          <div className="relative group">
            <button className="flex items-center space-x-1 text-gray-700 hover:text-brand-blue">
              <span>For Sellers</span>
              <ChevronDown size={16} />
            </button>
            <div className="absolute h-4 w-full"></div>
            <div className="absolute z-10 invisible group-hover:visible hover:visible bg-white shadow-md rounded-md w-48 mt-1 py-2">
              <Link to="/seller-dashboard" className="block px-4 py-2 hover:bg-gray-50">Seller Dashboard</Link>
              <Link to="/create-offer" className="block px-4 py-2 hover:bg-gray-50">Create Offer</Link>
              <Link to="/create-custom-package" className="block px-4 py-2 hover:bg-gray-50">Create Custom Package</Link>
            </div>
          </div>

          <Link to="/pricing" className="text-gray-700 hover:text-brand-blue">Pricing</Link>
          
          <div className="flex items-center space-x-3">
            {user ? (
              <div className="flex items-center space-x-3">
                <Button variant="outline" className="flex items-center space-x-2" asChild>
                  <Link to="/profile">
                    <User size={16} />
                    <span>Profile</span>
                  </Link>
                </Button>
                <Button variant="outline" onClick={handleSignOut}>
                  Log Out
                </Button>
              </div>
            ) : (
              <>
                <Button variant="outline" asChild>
                  <Link to="/login">Log In</Link>
                </Button>
                <Button variant="default" className="bg-brand-blue hover:bg-brand-darkBlue" asChild>
                  <Link to="/signup">Sign Up</Link>
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Mobile menu button */}
        <div className="md:hidden">
          <button onClick={toggleMenu}>
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden px-4 py-3 bg-white border-t">
          <div className="flex flex-col space-y-3">
            <h3 className="font-medium text-gray-500 text-sm">For Buyers</h3>
            <Link to="/marketplace" className="block py-2 px-3 hover:bg-gray-50 rounded-md">Marketplace</Link>
            <Link to="/custom-packages" className="block py-2 px-3 hover:bg-gray-50 rounded-md">Custom Packages</Link>
            <Link to="/buyer-requests" className="block py-2 px-3 hover:bg-gray-50 rounded-md">Buyer Requests</Link>
            <Link to="/sellers" className="block py-2 px-3 hover:bg-gray-50 rounded-md">Find Sellers</Link>
            
            <h3 className="font-medium text-gray-500 text-sm mt-2">For Sellers</h3>
            <Link to="/seller-dashboard" className="block py-2 px-3 hover:bg-gray-50 rounded-md">Seller Dashboard</Link>
            <Link to="/create-offer" className="block py-2 px-3 hover:bg-gray-50 rounded-md">Create Offer</Link>
            <Link to="/create-custom-package" className="block py-2 px-3 hover:bg-gray-50 rounded-md">Create Custom Package</Link>
            
            <Link to="/pricing" className="block py-2 px-3 hover:bg-gray-50 rounded-md">Pricing</Link>
            
            <div className="flex flex-col space-y-2 pt-2">
              {user ? (
                <>
                  <Button variant="outline" className="w-full justify-center flex items-center space-x-2" asChild>
                    <Link to="/profile">
                      <User size={16} />
                      <span>Profile</span>
                    </Link>
                  </Button>
                  <Button className="w-full justify-center" onClick={handleSignOut}>
                    Log Out
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" className="w-full justify-center" asChild>
                    <Link to="/login">Log In</Link>
                  </Button>
                  <Button className="w-full justify-center bg-brand-blue hover:bg-brand-darkBlue" asChild>
                    <Link to="/signup">Sign Up</Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
